/**
 * Shared classification of "the learner took over" gestures during narration.
 *
 * Two narration surfaces need to agree on these rules:
 *
 * - An in-flight narration scroll animation cancels on **either** half: any
 *   takeover while the player is moving the viewport should stop it.
 * - Active playback pauses on meaningful interaction, while manual scrolling
 *   is handled separately when it changes the current narration block.
 *
 * One implementation of the classification is what keeps those two agreeing —
 * notably that a click on plain authored text is NOT a takeover while a click
 * on a real control is.
 */

export const COURSE_SCROLL_ROOT_SELECTOR = '[data-testid="course-canvas-scroll-root"]';

/** Marks the trigger controls (Accordion header, Tab, mobile select) of a Click-Expand block. */
const CLICK_EXPAND_TOPIC_CONTROL_SELECTOR = '[data-narration-topic-control="true"]';

const MEANINGFUL_INTERACTIVE_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  'summary',
  '[role="button"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="tab"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

const MANUAL_SCROLL_KEYS = new Set([
  'ArrowDown',
  'ArrowUp',
  'PageDown',
  'PageUp',
  'Home',
  'End',
  ' ',
]);

/** Pointer-down tolerance, in px, for the scroll root's own scrollbar gutter. */
const SCROLLBAR_REGION_WIDTH_PX = 20;

function isNarrationControl(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('[data-narration-control="true"]') !== null;
}

function isMeaningfulInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest(MEANINGFUL_INTERACTIVE_SELECTOR) !== null;
}

/** `wheel`/`touchmove` raised inside the player's scroll viewport. */
function isManualScrollGesture(event: Event): boolean {
  if (isNarrationControl(event.target)) return false;
  const target = event.target;
  if (!(target instanceof Element)) return false;
  return target.closest(COURSE_SCROLL_ROOT_SELECTOR) !== null;
}

/**
 * The scroll root's own scrollbar gutter — the one place a bare pointer-down
 * really is a scroll gesture rather than a click on content.
 */
function isScrollbarGutterPointerDown(event: PointerEvent): boolean {
  if (isNarrationControl(event.target)) return false;
  const target = event.target;
  if (!(target instanceof HTMLElement)) return false;
  const scrollRoot = target.closest<HTMLElement>(COURSE_SCROLL_ROOT_SELECTOR);
  return (
    scrollRoot !== null
    && target === scrollRoot
    && event.clientX >= scrollRoot.getBoundingClientRect().right - SCROLLBAR_REGION_WIDTH_PX
  );
}

type Detach = () => void;

/**
 * Scroll-intent listeners: wheel/touch inside the scroll viewport, the
 * manual-scroll keys, and a pointer-down in the scrollbar gutter.
 */
function attachScrollIntentListeners(onScrollIntent: (event: Event) => void): Detach {
  const handleScrollGesture = (event: Event): void => {
    if (isManualScrollGesture(event)) onScrollIntent(event);
  };
  const handleKeyDown = (event: KeyboardEvent): void => {
    if (isNarrationControl(event.target)) return;
    if (MANUAL_SCROLL_KEYS.has(event.key)) onScrollIntent(event);
  };
  const handlePointerDown = (event: PointerEvent): void => {
    if (isScrollbarGutterPointerDown(event)) onScrollIntent(event);
  };

  document.addEventListener('wheel', handleScrollGesture, true);
  document.addEventListener('touchmove', handleScrollGesture, true);
  document.addEventListener('keydown', handleKeyDown, true);
  document.addEventListener('pointerdown', handlePointerDown, true);
  return () => {
    document.removeEventListener('wheel', handleScrollGesture, true);
    document.removeEventListener('touchmove', handleScrollGesture, true);
    document.removeEventListener('keydown', handleKeyDown, true);
    document.removeEventListener('pointerdown', handlePointerDown, true);
  };
}

/**
 * True when `target` is a topic trigger of the Click-Expand block `blockId`.
 * Such a gesture is narration's own topic step, not a takeover of the block.
 */
export function isClickExpandTopicControlOf(target: EventTarget | null, blockId: string): boolean {
  if (!(target instanceof Element)) return false;
  const control = target.closest(CLICK_EXPAND_TOPIC_CONTROL_SELECTOR);
  if (control === null) return false;
  return control.closest<HTMLElement>('[data-block-id]')?.dataset.blockId === blockId;
}

/**
 * Meaningful-interaction listeners: activating a real control outside the
 * narration cluster, by pointer or keyboard. Never a bare click on content.
 * The callback receives the capture-phase event so callers can tell targets apart.
 */
export function attachMeaningfulInteractionListeners(onInteraction: (event: Event) => void): Detach {
  const handleKeyDown = (event: KeyboardEvent): void => {
    if (isNarrationControl(event.target)) return;
    if (isMeaningfulInteractiveTarget(event.target)) onInteraction(event);
  };
  const handlePointerDown = (event: PointerEvent): void => {
    if (isNarrationControl(event.target)) return;
    if (isMeaningfulInteractiveTarget(event.target)) onInteraction(event);
  };

  document.addEventListener('keydown', handleKeyDown, true);
  document.addEventListener('pointerdown', handlePointerDown, true);
  return () => {
    document.removeEventListener('keydown', handleKeyDown, true);
    document.removeEventListener('pointerdown', handlePointerDown, true);
  };
}

/**
 * Both halves together for narration scroll animations, which cancel on any
 * learner takeover while the player is moving the viewport. The callback
 * receives the capture-phase event so callers can tell targets apart.
 */
export function attachManualIntentListeners(onManualIntent: (event: Event) => void): Detach {
  const detachScroll = attachScrollIntentListeners(onManualIntent);
  const detachInteraction = attachMeaningfulInteractionListeners(onManualIntent);
  return () => {
    detachScroll();
    detachInteraction();
  };
}
